import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Eye,
  Edit,
  Send,
  CalendarDays,
  Briefcase,
} from "lucide-react";
import { safeFormatDate } from "../../../../utils/dateUtils";
import {
  ProjectStatusInfo,
  getProjectStatusInfo,
  needsApproval,
  getNormalizedApprovalStatus,
  ProjectStatusTone,
} from "../../utils/projectStatusUtils";

interface ProjectCardProps {
  project: any;
  /** @deprecated retained for backwards compatibility — no longer rendered on the card */
  creatorName?: string;
  onView: (project: any) => void;
  onEdit?: (project: any) => void;
  onOpenAssetDrawer?: (project: any) => void;
  onOpenCalendar?: (project: any) => void;
  onSubmitForApproval?: (project: any) => void;
  isRequester?: boolean;
  hideStatus?: boolean;
  isAms?: boolean;
  /** AMS only: per-project audit-approval counts, rendered as status chips (like the approvals card). */
  auditCounts?: { pending: number; rejected: number; approved: number } | null;
}

type Theme = {
  /** Accent colour used for the side bar, code chip, and timeline fill */
  accent: string;
  accentSoft: string;
  accentBorder: string;
  /** Status pill */
  statusBg: string;
  statusBorder: string;
  statusText: string;
  /** Status dot fill (slightly more saturated than the pill text) */
  statusDot: string;
};

const THEME_BY_TONE: Record<ProjectStatusTone, Theme> = {
  ready: {
    accent: "#2563EB",
    accentSoft: "#EFF6FF",
    accentBorder: "#BFDBFE",
    statusBg: "#EFF6FF",
    statusBorder: "#BFDBFE",
    statusText: "#1D4ED8",
    statusDot: "#3B82F6",
  },
  notReady: {
    accent: "#D97706",
    accentSoft: "#FFFBEB",
    accentBorder: "#FDE68A",
    statusBg: "#FFFBEB",
    statusBorder: "#FDE68A",
    statusText: "#B45309",
    statusDot: "#F59E0B",
  },
  review: {
    accent: "#CA8A04",
    accentSoft: "#FEFCE8",
    accentBorder: "#FEF08A",
    statusBg: "#FEFCE8",
    statusBorder: "#FEF08A",
    statusText: "#854D0E",
    statusDot: "#EAB308",
  },
  rejected: {
    accent: "#DC2626",
    accentSoft: "#FEF2F2",
    accentBorder: "#FECACA",
    statusBg: "#FEF2F2",
    statusBorder: "#FECACA",
    statusText: "#B91C1C",
    statusDot: "#EF4444",
  },
  approved: {
    accent: "#059669",
    accentSoft: "#ECFDF5",
    accentBorder: "#A7F3D0",
    statusBg: "#ECFDF5",
    statusBorder: "#A7F3D0",
    statusText: "#047857",
    statusDot: "#10B981",
  },
  started: {
    accent: "#15803D",
    accentSoft: "#F0FDF4",
    accentBorder: "#BBF7D0",
    statusBg: "#F0FDF4",
    statusBorder: "#BBF7D0",
    statusText: "#15803D",
    statusDot: "#22C55E",
  },
  default: {
    accent: "#6B7280",
    accentSoft: "#F3F4F6",
    accentBorder: "#E5E7EB",
    statusBg: "#F3F4F6",
    statusBorder: "#E5E7EB",
    statusText: "#374151",
    statusDot: "#9CA3AF",
  },
};

const computeTimelineProgress = (
  startRaw: any,
  endRaw: any
): { hasRange: boolean; pct: number; status: "before" | "during" | "after" } => {
  const start = startRaw ? new Date(startRaw) : null;
  const end = endRaw ? new Date(endRaw) : null;
  if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) {
    return { hasRange: false, pct: 0, status: "before" };
  }
  const today = Date.now();
  const span = end.getTime() - start.getTime();
  if (span <= 0) {
    return { hasRange: true, pct: 100, status: today < start.getTime() ? "before" : "after" };
  }
  if (today < start.getTime()) return { hasRange: true, pct: 0, status: "before" };
  if (today > end.getTime()) return { hasRange: true, pct: 100, status: "after" };
  const pct = ((today - start.getTime()) / span) * 100;
  return { hasRange: true, pct, status: "during" };
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onView,
  onEdit,
  onOpenAssetDrawer,
  onOpenCalendar,
  onSubmitForApproval,
  isRequester = false,
  hideStatus = false,
  isAms = false,
  auditCounts = null,
}) => {
  const auditChips =
    isAms && auditCounts
      ? ([
          ['pending', auditCounts.pending, 'text-amber-700 bg-amber-50 border-amber-200', 'bg-amber-500'],
          ['rejected', auditCounts.rejected, 'text-red-700 bg-red-50 border-red-200', 'bg-red-500'],
          ['approved', auditCounts.approved, 'text-emerald-700 bg-emerald-50 border-emerald-200', 'bg-emerald-500'],
        ] as const).filter(([, n]) => (n as number) > 0)
      : [];
  const statusInfo: ProjectStatusInfo = getProjectStatusInfo(project);
  const theme = THEME_BY_TONE[statusInfo.tone] || THEME_BY_TONE.default;
  const isNotApproved = needsApproval(project);
  const approvalStatus = getNormalizedApprovalStatus(project);
  const canEdit =
    approvalStatus === "draft" ||
    approvalStatus === "rejected" ||
    approvalStatus === "not_required" ||
    !approvalStatus;

  const startDate =
    project?.actualStartDate || project?.startDate || project?.plannedStartDate;
  const endDate =
    project?.actualEndDate || project?.endDate || project?.plannedEndDate;

  const timeline = computeTimelineProgress(startDate, endDate);

  const projectTypeLabel =
    typeof project?.projectTypeId === "object" && project?.projectTypeId?.name
      ? project.projectTypeId.name
      : project?.projectType || "";

  const locationLabel =
    project?.locationId?.name || project?.location || "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="group relative flex bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_0_0_1px_rgba(15,23,42,0.02)] hover:shadow-[0_18px_36px_-12px_rgba(15,23,42,0.16),0_4px_10px_-4px_rgba(15,23,42,0.08)] hover:border-gray-300 transition-all cursor-pointer overflow-hidden font-[family-name:var(--font-outfit)]"
      onClick={() => onOpenAssetDrawer?.(project)}
      title="Click to manage assets and equipment"
    >
      {/* Status accent (left edge, full height) */}
      <div
        aria-hidden
        className="w-[5px] flex-shrink-0"
        style={{
          background: `linear-gradient(180deg, ${theme.accent}, ${theme.statusDot})`,
        }}
      />

      {/* Content column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header: code + status pill */}
        <div className="flex items-start justify-between gap-2 px-5 pt-4">
          <span
            className="inline-flex items-center px-2 py-[3px] rounded-md text-[10.5px] font-bold tracking-[0.08em] uppercase font-mono tabular-nums border"
            style={{
              background: theme.accentSoft,
              color: theme.accent,
              borderColor: theme.accentBorder,
            }}
            title={project?.projectId || ""}
          >
            {project?.projectId || "NEW"}
          </span>

          <div className="flex items-center gap-1.5 min-w-0">
            {isAms && project?.requirement && (
              <span
                className={`inline-flex items-center px-2.5 py-[3px] rounded-full text-[10.5px] font-semibold tracking-tight border whitespace-nowrap uppercase ${
                  project.requirement === "known"
                    ? "bg-blue-50 text-blue-700 border-blue-200"
                    : "bg-indigo-50 text-indigo-700 border-indigo-200"
                }`}
                title="AMS scope requirement"
              >
                {project.requirement}
              </span>
            )}
            {!hideStatus && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full text-[10.5px] font-semibold tracking-tight border whitespace-nowrap"
                style={{
                  background: theme.statusBg,
                  color: theme.statusText,
                  borderColor: theme.statusBorder,
                }}
                title={statusInfo.description || statusInfo.label}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: theme.statusDot }}
                />
                <span className="truncate max-w-[140px]">{statusInfo.label}</span>
              </span>
            )}
          </div>
        </div>

        {/* Title */}
        <h3
          className="px-5 pt-2.5 text-[15.5px] font-semibold text-gray-900 leading-snug tracking-tight line-clamp-2"
          title={project?.projectName || project?.name}
        >
          {project?.projectName || project?.name}
        </h3>

        {/* Meta row: type · location */}
        {(projectTypeLabel || locationLabel) && (
          <div className="px-5 pt-2 flex items-center gap-2 flex-wrap text-[11.5px] text-gray-500 min-w-0">
            {projectTypeLabel && (
              <span className="inline-flex items-center gap-1 max-w-full min-w-0">
                <Briefcase
                  size={12}
                  strokeWidth={2.4}
                  className="text-gray-400 flex-shrink-0"
                />
                <span className="truncate font-medium text-gray-700">
                  {projectTypeLabel}
                </span>
              </span>
            )}
            {projectTypeLabel && locationLabel && (
              <span className="text-gray-300 select-none">·</span>
            )}
            {locationLabel && (
              <span className="inline-flex items-center gap-1 max-w-full min-w-0">
                <MapPin
                  size={12}
                  strokeWidth={2.4}
                  className="text-gray-400 flex-shrink-0"
                />
                <span className="truncate">{locationLabel}</span>
              </span>
            )}
          </div>
        )}

        {/* AMS audit-status chips — mirrors the approvals card so the two views read alike */}
        {auditChips.length > 0 && (
          <div className="px-5 pt-2 flex flex-wrap gap-1.5">
            {auditChips.map(([key, n, cls, dot]) => (
              <span
                key={key}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-[2px] text-[10.5px] font-semibold ${cls}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                {n} {key}
              </span>
            ))}
          </div>
        )}

        {/* Rejection reason — surfaced on OMS cards so the requester sees why.
            On AMS cards the reason concatenates every audit's reject comment (noisy),
            so we show only the "Rejected" status chip; full reasons live in the drawer. */}
        {!isAms &&
          statusInfo.tone === "rejected" &&
          (project?.rejectionReason || project?.projectLevelApproverComments) && (
            <div className="mx-5 mt-2 text-[11px] text-red-700 bg-red-50 border border-red-100 rounded-md px-2 py-1">
              <span className="font-semibold">Rejected: </span>
              {project.rejectionReason || project.projectLevelApproverComments}
            </div>
          )}

        {/* Spacer pushes timeline to bottom */}
        <div className="flex-1" />

        {/* Footer: timeline + inline actions */}
        <div className="px-5 pt-4 pb-4 mt-3 border-t border-gray-100 flex items-end gap-3">
          <div className="flex-1 min-w-0">
            {timeline.hasRange ? (
              <div className="space-y-1.5">
                <div className="relative h-1.5 rounded-full bg-gray-100">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500"
                    style={{
                      width: `${timeline.pct}%`,
                      background: `linear-gradient(90deg, ${theme.accent}, ${theme.statusDot})`,
                    }}
                  />
                  <div
                    className="absolute -top-[5px] -translate-x-1/2 w-4 h-4 rounded-full border-[2.5px] border-white shadow-[0_2px_6px_rgba(15,23,42,0.18)]"
                    style={{
                      left: `${Math.max(2, Math.min(98, timeline.pct))}%`,
                      background: theme.statusDot,
                    }}
                    title={`Today (${timeline.status === "before" ? "not started" : timeline.status === "after" ? "past end date" : `${Math.round(timeline.pct)}% elapsed`})`}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] tabular-nums text-gray-500">
                  <span title="Start date" className="font-medium">
                    {safeFormatDate(startDate, "MMM d, yyyy")}
                  </span>
                  <span title="End date" className="font-medium">
                    {safeFormatDate(endDate, "MMM d, yyyy")}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-[10.5px] text-gray-400 h-[26px]">
                <CalendarDays size={11} strokeWidth={2.4} />
                <span>No timeline set</span>
              </div>
            )}
          </div>

          {/* Action cluster — flat segmented group with subtle dividers */}
          <div className="flex items-center divide-x divide-gray-200 rounded-lg border border-gray-200 bg-white overflow-hidden flex-shrink-0">
            {onOpenCalendar && (startDate || endDate) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenCalendar(project);
                }}
                className="px-2 py-1.5 text-sky-600 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                title="Planning calendar"
              >
                <CalendarDays size={13} strokeWidth={2.4} />
              </button>
            )}
            {isRequester && isNotApproved && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onSubmitForApproval?.(project);
                }}
                className="px-2 py-1.5 text-orange-600 hover:bg-orange-50 hover:text-orange-700 transition-colors"
                style={{
                  color: project.approvalStatus === "rejected" ? "#dc2626" : undefined,
                }}
                title={
                  project.approvalStatus === "rejected"
                    ? "Resubmit for approval"
                    : "Submit for approval"
                }
              >
                <Send size={13} strokeWidth={2.4} />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(project);
              }}
              className="px-2 py-1.5 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
              title="View details"
            >
              <Eye size={13} strokeWidth={2.4} />
            </button>
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (canEdit) onEdit(project);
                }}
                disabled={!canEdit}
                className={`px-2 py-1.5 transition-colors ${
                  canEdit
                    ? "text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                    : "text-gray-300 cursor-not-allowed"
                }`}
                title={canEdit ? "Edit" : "Edit disabled"}
              >
                <Edit size={13} strokeWidth={2.4} />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;
